// REST 封装：Bearer、401 自动刷新一次后重试、统一错误体 `{code, message, details?}`。

import { auth } from "./auth";

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details?: unknown,
  ) {
    super(message);
  }
}

export type Query = Record<string, string | number | boolean | null | undefined>;

export type RequestOpts = {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  query?: Query;
  signal?: AbortSignal;
  /** 默认 true；登录/刷新本身不带令牌 */
  auth?: boolean;
  /** 页面关闭时仍发出（释放租约） */
  keepalive?: boolean;
  /** 超时（毫秒），默认 20 s；挂住的请求不能让调用方无限等 */
  timeoutMs?: number;
};

const DEFAULT_TIMEOUT_MS = 20_000;

export function buildUrl(path: string, query?: Query): string {
  if (!query) return path;
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v !== undefined && v !== null && v !== "") qs.set(k, String(v));
  }
  const s = qs.toString();
  return s ? `${path}?${s}` : path;
}

async function send(path: string, o: RequestOpts, token: string | null): Promise<Response> {
  const headers: Record<string, string> = { Accept: "application/json" };
  if (o.body !== undefined) headers["Content-Type"] = "application/json";
  if (token) headers.Authorization = `Bearer ${token}`;
  return fetch(buildUrl(path, o.query), {
    method: o.method ?? "GET",
    headers,
    body: o.body === undefined ? undefined : JSON.stringify(o.body),
    signal: o.signal ?? AbortSignal.timeout(o.timeoutMs ?? DEFAULT_TIMEOUT_MS),
    keepalive: o.keepalive,
  });
}

async function toError(r: Response): Promise<ApiError> {
  try {
    const b = (await r.json()) as { code?: string; message?: string; details?: unknown };
    return new ApiError(r.status, b.code ?? "http_error", b.message ?? r.statusText, b.details);
  } catch {
    return new ApiError(r.status, r.status >= 500 ? "server_error" : "http_error", r.statusText);
  }
}

export async function api<T>(path: string, o: RequestOpts = {}): Promise<T> {
  const useAuth = o.auth !== false;
  let token = useAuth ? await auth.accessToken() : null;
  let r = await send(path, o, token);
  if (r.status === 401 && useAuth) {
    // 令牌过期/被吊销：刷新一次再试；刷新失败即登出
    token = await auth.forceRefresh(token);
    if (!token) throw await toError(r);
    r = await send(path, o, token);
  }
  if (!r.ok) {
    const e = await toError(r);
    if (r.status === 401 && useAuth) auth.expire();
    if (r.status === 403 && e.code === "auth.totp_setup_required") auth.totpSetupRequired();
    if (r.status === 403 && e.code === "auth.password_change_required") auth.passwordChangeRequired();
    throw e;
  }
  if (r.status === 204) return undefined as T;
  return (await r.json()) as T;
}
